import React, { useRef, useEffect, Fragment } from 'react'
import { useReadOnly, ReactEditor } from 'vendor/slate-react'
import { Path, Node, Transforms, Editor } from 'vendor/slate'
import styled from 'styled-components'
import { ELEMENT_TYPES } from 'Documents/editor/slate/types'
import { v4 as uuidv4 } from 'uuid'
import { DocumentTh } from 'cg/components/documents/editor/slate/documentStyles'

const TableHeader = ({ attributes, children, element, editor }) => {
  const readOnly = useReadOnly()
  const { addColumnsDisabled } = element

  const deleteTableColumn = () => {
    const path = ReactEditor.findPath(editor, element)
    const parentPath = Node.has(editor, path) ? Path.parent(path) : null
    const parent = Node.has(editor, parentPath) ? Node.parent(editor, path) : null
    const grandParentPath = Node.has(editor, parentPath) ? Path.parent(parentPath) : null
    const grandParent = grandParentPath ? Node.get(editor, grandParentPath) : null
    const tablePath = Node.has(editor, grandParentPath) ? Path.parent(grandParentPath) : null
    const table = tablePath ? Node.get(editor, tablePath) : null

    if (parent && parent.children && parent.children.length > 1 && grandParent && table) {
      const columnIndex = path[path.length - 1]

      const theadPath = ReactEditor.findPath(editor, grandParent.children[0].children[columnIndex])
      const trows = table.children[1].children

      Transforms.removeNodes(editor, { at: theadPath })
      trows.forEach(row => {
        const tdPath = ReactEditor.findPath(editor, row.children[columnIndex])
        Transforms.removeNodes(editor, { at: tdPath })
      })
    } else {
      Transforms.removeNodes(editor, { at: tablePath })
    }
  }

  const insertTableColumn = () => {
    const path = ReactEditor.findPath(editor, element)
    const parentPath = Node.has(editor, path) ? Path.parent(path) : null
    const grandParentPath = Node.has(editor, parentPath) ? Path.parent(parentPath) : null
    const grandParent = grandParentPath ? Node.get(editor, grandParentPath) : null
    const tablePath = Node.has(editor, grandParentPath) ? Path.parent(grandParentPath) : null
    const table = tablePath ? Node.get(editor, tablePath) : null

    const columnIndex = path[path.length - 1]

    const theadPath = grandParent ? ReactEditor.findPath(editor, grandParent.children[0].children[columnIndex]) : null
    const trows = table ? table.children[1].children : []

    Editor.withoutNormalizing(editor, () => {
      if (theadPath) {
        Transforms.insertNodes(
          editor,
          {
            id: `th-${uuidv4()}`,
            type: ELEMENT_TYPES.table_th,
            parent: { type: ELEMENT_TYPES.table_tr },
            children: [
              {
                type: ELEMENT_TYPES.default,
                parent: { type: ELEMENT_TYPES.table_th },
                children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
              }
            ]
          },
          { at: Path.next(theadPath) }
        )
      }

      trows.forEach(row => {
        const tdPath = ReactEditor.findPath(editor, row.children[columnIndex])
        Transforms.insertNodes(
          editor,
          {
            id: `tr-${uuidv4()}`,
            type: ELEMENT_TYPES.table_td,
            parent: { type: ELEMENT_TYPES.table_tr },
            children: [
              {
                type: ELEMENT_TYPES.default,
                parent: { type: ELEMENT_TYPES.table_td },
                children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
              }
            ]
          },
          { at: Path.next(tdPath) }
        )
      })
    })
  }

  return (
    <TH {...attributes} readOnly={readOnly}>
      {children}
      {!readOnly && (
        <>
          {!addColumnsDisabled && (
            <TableDeleteColumnContainer contentEditable={false} style={{ userSelect: 'none' }}>
              <TableDeleteColumn
                onClick={() => {
                  deleteTableColumn()
                }}
              >
                <i className="fad fa-trash" />
              </TableDeleteColumn>
            </TableDeleteColumnContainer>
          )}
        </>
      )}
      {!readOnly && (
        <>
          {!addColumnsDisabled && (
            <TableAddColumn
              onClick={() => {
                insertTableColumn()
              }}
              contentEditable={false}
              style={{ userSelect: 'none' }}
            >
              <i className="far fa-plus" />
            </TableAddColumn>
          )}
        </>
      )}
    </TH>
  )
}

export default TableHeader

const TableDeleteColumn = styled.div`
  width: 100%;
  border-radius: 4px;
  background: ${props => props.theme.bgLightestInverse};
  color: ${props => props.theme.textGreyTintedLighter};
  font-size: 13.5px;
  height: 21px;
  padding: 4px;
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  -webkit-transition: 0.15s;
  transition: 0.15s;
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const TableAddColumn = styled.div`
  width: 20px;
  height: 20px;
  font-size: 13px;
  border-radius: 4px;
  position: absolute;
  cursor: pointer;
  right: -10px;
  top: calc(50% - 10px);
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.bgLightest};
  border: 1px solid ${props => props.theme.borderColor};
  color: ${props => props.theme.textGreyTinted};
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.05);
  -webkit-transition: 0.15s;
  transition: 0.15s;
  display: none;
  z-index: 2;
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`
const TableDeleteColumnContainer = styled.div`
  width: 100%;
  top: -28px;
  height: 33px;
  left: 0px;
  display: none;
  position: absolute;
  &:hover {
    ${TableDeleteColumn} {
      display: flex;
    }
  }
`

const TH = styled(DocumentTh)`
  &:hover {
    ${TableDeleteColumnContainer} {
      display: flex;
    }
    ${TableAddColumn} {
      display: flex;
    }
  }
`
